import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  FormControlLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { omnichannelInboxApi } from "../../services/omnichannelInboxApi";

// ── Smart Handover Briefing helper ───────────────────────────────────────────
const buildHandoverBriefing = (conversation) => {
  if (!conversation) return null;

  const name = conversation?.customer?.name || conversation?.customer_name || "Customer";
  const channel = String(conversation?.channel || "").toLowerCase();
  const subject = conversation?.subject || conversation?.last_message_preview || "";
  const status = conversation?.status || "";
  const priority = conversation?.priority || "";
  const inquiry = conversation?.inquiry || null;

  // Last customer message
  const messages = Array.isArray(conversation?.messages) ? conversation.messages : [];
  const lastCustomerMsg = messages
    .filter((m) => m?.direction === "inbound" && (m?.body?.text || m?.body))
    .slice(-1)[0];
  const lastMsgText = lastCustomerMsg?.body?.text || lastCustomerMsg?.body || "";

  // Last internal note
  const lastNote = messages
    .filter((m) => String(m?.message_type || "").toLowerCase() === "internal_note" && (m?.body?.text || m?.body))
    .slice(-1)[0];
  const lastNoteText = lastNote?.body?.text || lastNote?.body || "";

  // SLA
  const slaStatus = conversation?.sla_status || "";
  const dueAt = conversation?.first_response_due_at;

  // Quote info
  const quote = conversation?.active_quote || null;
  const quoteDetection = conversation?.quote_detection || null;

  const lines = [];
  lines.push(`Customer: ${name}`);
  if (channel) lines.push(`Channel: ${channel}`);
  if (subject) lines.push(`Thread: ${subject.slice(0, 120)}`);
  lines.push(`Status: ${status || "open"}${priority ? ` · Priority: ${priority}` : ""}`);

  if (quote) {
    const q = [quote.status_label || "Quote tracked"];
    if (quote.route_label) q.push(quote.route_label);
    if (quote.travel_date_text) q.push(quote.travel_date_text);
    lines.push(`Quote: ${q.join(" · ")}`);
  } else if (quoteDetection?.prompt_create || quoteDetection?.auto_create) {
    lines.push("Quote: Potential quote inquiry detected — not yet created.");
  }

  if (inquiry?.inquiry_status) lines.push(`Inquiry: ${inquiry.inquiry_status}`);

  if (lastMsgText) {
    const preview = lastMsgText.slice(0, 160);
    lines.push(`Last customer message: "${preview}${lastMsgText.length > 160 ? "…" : ""}"`);
  }

  if (lastNoteText) {
    const preview = lastNoteText.slice(0, 120);
    lines.push(`Team note: "${preview}${lastNoteText.length > 120 ? "…" : ""}"`);
  }

  if (slaStatus && slaStatus !== "ok") {
    lines.push(`SLA: ${slaStatus}${dueAt ? ` (due ${new Date(dueAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })})` : ""}`);
  }

  lines.push("Recommended: Review the above context before responding.");

  return lines.join("\n");
};

export function LinkInquiryDialog({ open, onClose, conversation, onLinked }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
      setLoading(false);
      setSaving(false);
      setSelectedId("");
      setError("");
      return;
    }

    const initialQuery =
      conversation?.customer?.email ||
      conversation?.customer?.phone ||
      conversation?.customer?.name ||
      "";

    setQuery(initialQuery);
  }, [open, conversation]);

  useEffect(() => {
    if (!open || !query || query.trim().length < 1) {
      setResults([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    const timer = setTimeout(async () => {
      try {
        const res = await omnichannelInboxApi.searchInquiries({ q: query.trim(), limit: 10 });
        if (cancelled) return;
        setResults(Array.isArray(res?.data) ? res.data : []);
      } catch (err) {
        if (cancelled) return;
        setError(err?.response?.data?.message || err?.message || "Failed to search inquiries.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, query]);

  const handleSave = async () => {
    if (!selectedId) return;

    setSaving(true);
    setError("");

    try {
      const res = await omnichannelInboxApi.linkConversationToInquiry(conversation.id, selectedId);
      onLinked?.(res);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to link conversation.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Link Conversation To Inquiry</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            Search the existing inquiry engine and attach this conversation to the correct inquiry record.
          </Typography>

          <TextField
            autoFocus
            label="Search inquiries"
            placeholder="Search by inquiry ID, name, email, or phone"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            fullWidth
          />

          {error ? <Alert severity="error">{error}</Alert> : null}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : !results.length ? (
            <Alert severity="info">No matching inquiries found yet.</Alert>
          ) : (
            <RadioGroup value={String(selectedId)}>
              <List disablePadding sx={{ border: "1px solid #E8ECF1", borderRadius: 2, overflow: "hidden" }}>
                {results.map((item) => (
                  <ListItemButton key={item.id} onClick={() => setSelectedId(String(item.id))} divider>
                    <Radio value={String(item.id)} checked={String(selectedId) === String(item.id)} />
                    <ListItemText
                      primary={`#${item.id} · ${item.full_name}`}
                      secondary={[
                        item.email,
                        item.phone,
                        item.status,
                        item.assigned_to?.name ? `Assigned: ${item.assigned_to.name}` : "Unassigned",
                      ].filter(Boolean).join(" • ")}
                    />
                  </ListItemButton>
                ))}
              </List>
            </RadioGroup>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || !selectedId}>
          {saving ? "Linking..." : "Link Inquiry"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function AssignConversationDialog({ open, onClose, conversation, onAssigned }) {
  // Named AssignConversationDialog for backward compat — UI shows "Handover"
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [syncInquiry, setSyncInquiry] = useState(true);
  const [error, setError] = useState("");
  const [briefingExpanded, setBriefingExpanded] = useState(true);
  const [copied, setCopied] = useState(false);

  const briefing = useMemo(() => buildHandoverBriefing(conversation), [conversation]);

  const handleCopyBriefing = () => {
    if (!briefing) return;
    navigator.clipboard?.writeText(briefing).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  useEffect(() => {
    if (!open) {
      setUsers([]);
      setLoading(false);
      setSaving(false);
      setError("");
      setSelectedId("");
      setSyncInquiry(true);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setSelectedId(String(conversation?.assignment?.assigned_to?.id || conversation?.inquiry?.assigned_to?.id || ""));

    omnichannelInboxApi
      .listAssignableUsers({ workstream_id: conversation?.workstream?.id })
      .then((res) => {
        if (cancelled) return;
        setUsers(Array.isArray(res?.data) ? res.data : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || err?.message || "Failed to load assignable users.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, conversation]);

  const handleSave = async () => {
    if (!conversation?.id) {
      setError("Conversation ID is missing. Please close and reopen this dialog.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const assignedTo = selectedId ? Number(selectedId) : null;
      const res = await omnichannelInboxApi.assignConversation(conversation.id, assignedTo, syncInquiry);
      onAssigned?.(res);
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "";
      setError(
        msg.toLowerCase().includes("not found")
          ? "This conversation no longer exists. Please refresh the inbox."
          : msg || "Failed to hand over conversation."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Handover Conversation</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            Hand over this conversation to another team member and optionally sync the linked inquiry assignment.
          </Typography>

          {/* ── Smart Handover Briefing ── */}
          {briefing && (
            <Box sx={{ border: "1px solid #fde68a", borderRadius: 2, overflow: "hidden" }}>
              <Box
                onClick={() => setBriefingExpanded((p) => !p)}
                sx={{
                  px: 1.5, py: 1,
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  bgcolor: "#fffbeb", cursor: "pointer", userSelect: "none",
                }}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#92400e", display: "flex", alignItems: "center", gap: 0.7 }}>
                  📋 Smart Handover Briefing
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Tooltip title={copied ? "Copied!" : "Copy briefing"}>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleCopyBriefing(); }}
                      sx={{ color: "#92400e", width: 26, height: 26 }}
                    >
                      <ContentCopyRoundedIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Tooltip>
                  {briefingExpanded
                    ? <ExpandLessRoundedIcon sx={{ fontSize: 16, color: "#92400e" }} />
                    : <ExpandMoreRoundedIcon sx={{ fontSize: 16, color: "#92400e" }} />
                  }
                </Box>
              </Box>
              <Collapse in={briefingExpanded}>
                <Box sx={{ px: 1.5, py: 1, bgcolor: "#fefce8", borderTop: "1px solid #fef08a" }}>
                  {briefing.split("\n").map((line, i) => {
                    const colonIdx = line.indexOf(":");
                    const label = colonIdx > 0 ? line.slice(0, colonIdx) : null;
                    const value = colonIdx > 0 ? line.slice(colonIdx + 1).trim() : line;
                    return (
                      <Box key={i} sx={{ display: "flex", gap: 0.5, mb: 0.4, fontSize: 12, lineHeight: 1.5 }}>
                        {label && (
                          <Box component="span" sx={{ fontWeight: 700, color: "#78350f", flexShrink: 0, minWidth: 80 }}>
                            {label}:
                          </Box>
                        )}
                        <Box component="span" sx={{ color: "#292524" }}>{value}</Box>
                      </Box>
                    );
                  })}
                </Box>
              </Collapse>
            </Box>
          )}

          {error ? <Alert severity="error">{error}</Alert> : null}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <RadioGroup value={String(selectedId)} onChange={(e) => setSelectedId(e.target.value)}>
                <List disablePadding sx={{ border: "1px solid #E8ECF1", borderRadius: 2, overflow: "hidden" }}>
                  <ListItemButton onClick={() => setSelectedId("")} divider>
                    <Radio value="" checked={selectedId === ""} />
                    <ListItemText primary="Unassigned" secondary="Keep this conversation in the shared queue." />
                  </ListItemButton>

                  {users.map((user) => (
                    <ListItemButton key={user.id} onClick={() => setSelectedId(String(user.id))} divider>
                      <Radio value={String(user.id)} checked={String(selectedId) === String(user.id)} />
                      <ListItemText primary={user.name} secondary={`${user.role} • ${user.email}`} />
                    </ListItemButton>
                  ))}
                </List>
              </RadioGroup>

              <FormControlLabel
                control={<Switch checked={syncInquiry} onChange={(e) => setSyncInquiry(e.target.checked)} />}
                label="Sync handover to linked inquiry"
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || loading}>
          {saving ? "Handing over..." : "Confirm Handover"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ReplyConversationDialog({ open, onClose, conversation, channelLabel, onSent }) {
  const [body, setBody] = useState("");
  const [subject, setSubject] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [cannedReplies, setCannedReplies] = useState([]);
  const [cannedLoading, setCannedLoading] = useState(false);
  const [cannedError, setCannedError] = useState("");
  const [selectedReplyId, setSelectedReplyId] = useState("");
  const [templateTitle, setTemplateTitle] = useState("");
  const [templateScope, setTemplateScope] = useState("personal");
  const [templateSaving, setTemplateSaving] = useState(false);

  const replyState = conversation?.reply || {};
  const isEmail = String(conversation?.channel || "").toLowerCase() === "email";

  useEffect(() => {
    if (!open) {
      setBody("");
      setSubject("");
      setSaving(false);
      setError("");
      setCannedReplies([]);
      setCannedLoading(false);
      setCannedError("");
      setSelectedReplyId("");
      setTemplateTitle("");
      setTemplateScope("personal");
      setTemplateSaving(false);
      return;
    }

    setBody("");
    setSubject(
      isEmail
        ? conversation?.subject
          ? `Re: ${conversation.subject}`
          : "Re: Your inquiry"
        : ""
    );
    setError("");
  }, [open, conversation, isEmail]);

  useEffect(() => {
    if (!open || !conversation?.id) return;

    let cancelled = false;
    setCannedLoading(true);
    setCannedError("");

    omnichannelInboxApi
      .listCannedReplies({
        channel: conversation?.channel,
        workstream_id: conversation?.workstream?.id,
      })
      .then((res) => {
        if (cancelled) return;
        setCannedReplies(Array.isArray(res?.data) ? res.data : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setCannedError(err?.response?.data?.message || err?.message || "Failed to load canned replies.");
      })
      .finally(() => {
        if (!cancelled) setCannedLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, conversation]);

  const handleSelectCannedReply = (replyId) => {
    setSelectedReplyId(replyId);
    const cannedReply = cannedReplies.find((item) => String(item.id) === String(replyId));
    if (!cannedReply) return;

    setBody(cannedReply.body || "");
    if (!subject.trim() && isEmail && cannedReply.title) {
      setSubject(`Re: ${conversation?.subject || cannedReply.title}`);
    }
  };

  const handleSend = async () => {
    if (!conversation?.id || !body.trim()) return;

    setSaving(true);
    setError("");

    try {
      const res = await omnichannelInboxApi.sendConversationReply(conversation.id, {
        body: body.trim(),
        subject: isEmail ? subject.trim() : undefined,
      });
      onSent?.(res);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to send reply.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!conversation?.id || !body.trim()) return;

    setTemplateSaving(true);
    setError("");

    try {
      const response = await omnichannelInboxApi.createCannedReply({
        scope: templateScope,
        workstream_id: templateScope === "workstream" ? conversation?.workstream?.id : null,
        channel: conversation?.channel,
        title: templateTitle.trim() || `${channelLabel} reply`,
        body: body.trim(),
      });

      setCannedReplies((prev) => [...prev, response?.data].filter(Boolean));
      setTemplateTitle("");
      setTemplateScope("personal");
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to save canned reply.");
    } finally {
      setTemplateSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Reply In {channelLabel}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            Send an operational reply from the omnichannel inbox using the configured provider for this channel.
          </Typography>

          {!replyState?.configured ? (
            <Alert severity="info">
              Provider sending is not configured for this channel yet. Configure credentials before sending live replies.
            </Alert>
          ) : null}

          {error ? <Alert severity="error">{error}</Alert> : null}
          {cannedError ? <Alert severity="warning">{cannedError}</Alert> : null}

          <TextField
            select
            label="Saved replies"
            value={selectedReplyId}
            onChange={(e) => handleSelectCannedReply(e.target.value)}
            fullWidth
            disabled={cannedLoading}
            helperText={cannedLoading ? "Loading saved replies..." : "Insert a personal, workstream, or global reply template."}
          >
            <MenuItem value="">None</MenuItem>
            {cannedReplies.map((reply) => (
              <MenuItem key={reply.id} value={String(reply.id)}>
                {reply.title} ({reply.scope})
              </MenuItem>
            ))}
          </TextField>

          {isEmail ? (
            <TextField
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              fullWidth
            />
          ) : null}

          <TextField
            label="Reply"
            placeholder={isEmail ? "Write your email reply..." : "Write your WhatsApp reply..."}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            fullWidth
            multiline
            minRows={6}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              label="Save draft as template"
              value={templateTitle}
              onChange={(e) => setTemplateTitle(e.target.value)}
              fullWidth
              placeholder="Template title"
            />
            <TextField
              select
              label="Scope"
              value={templateScope}
              onChange={(e) => setTemplateScope(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="personal">Personal</MenuItem>
              <MenuItem value="workstream" disabled={!conversation?.workstream?.id}>
                Workstream
              </MenuItem>
            </TextField>
            <Button
              variant="outlined"
              onClick={handleSaveTemplate}
              disabled={templateSaving || !body.trim()}
              sx={{ flexShrink: 0 }}
            >
              {templateSaving ? "Saving..." : "Save Draft"}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          disabled={saving || !replyState?.configured || !body.trim()}
        >
          {saving ? "Sending..." : "Send Reply"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function InternalNoteDialog({ open, onClose, conversation, onSaved }) {
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setBody("");
      setSaving(false);
      setError("");
    }
  }, [open]);

  const handleSave = async () => {
    if (!conversation?.id || !body.trim()) return;

    setSaving(true);
    setError("");

    try {
      const res = await omnichannelInboxApi.createInternalNote(conversation.id, body.trim());
      onSaved?.(res);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to add note.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add Internal Note</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            Internal notes stay visible to staff only and never go out to the customer channel.
          </Typography>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <TextField
            label="Internal note"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            fullWidth
            multiline
            minRows={5}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || !body.trim()}>
          {saving ? "Saving..." : "Save Note"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function FollowUpDialog({ open, onClose, conversation, onSaved }) {
  const [dueAt, setDueAt] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setDueAt("");
      setNote("");
      setSaving(false);
      setError("");
      return;
    }

    const nextTask = conversation?.follow_ups?.[0];
    setDueAt(nextTask?.due_at ? new Date(nextTask.due_at).toISOString().slice(0, 16) : "");
    setNote(nextTask?.note || "");
  }, [open, conversation]);

  const handleSave = async () => {
    if (!conversation?.id) return;

    setSaving(true);
    setError("");

    try {
      const nextTask = conversation?.follow_ups?.[0];
      const res = await omnichannelInboxApi.saveFollowUp(conversation.id, {
        follow_up_id: nextTask?.id,
        due_at: dueAt ? new Date(dueAt).toISOString() : null,
        note,
        status: "open",
      });
      onSaved?.(res);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to save follow-up.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Manage Follow-up</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            Schedule the next action directly from the inbox so the conversation stays visible operationally.
          </Typography>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <TextField
            label="Due at"
            type="datetime-local"
            value={dueAt}
            onChange={(e) => setDueAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Follow-up note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            multiline
            minRows={4}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? "Saving..." : "Save Follow-up"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function InitiateVoiceCallDialog({ open, onClose, conversation, onSaved }) {
  const [telephonyAccounts, setTelephonyAccounts] = useState([]);
  const [telephonyLoading, setTelephonyLoading] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [toNumber, setToNumber] = useState("");
  const [bridgeToNumber, setBridgeToNumber] = useState("");
  const [recordingEnabled, setRecordingEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const selectedAccount = telephonyAccounts.find((item) => String(item.id) === String(selectedAccountId));

  useEffect(() => {
    if (!open) {
      setTelephonyAccounts([]);
      setTelephonyLoading(false);
      setSelectedAccountId("");
      setToNumber("");
      setBridgeToNumber("");
      setRecordingEnabled(false);
      setSaving(false);
      setError("");
      return;
    }

    let cancelled = false;
    setTelephonyLoading(true);
    setError("");
    setToNumber(conversation?.customer?.phone || conversation?.contact?.phone || "");

    omnichannelInboxApi
      .listTelephonyOptions()
      .then((res) => {
        if (cancelled) return;

        const nextAccounts = Array.isArray(res?.data) ? res.data : [];
        setTelephonyAccounts(nextAccounts);

        if (nextAccounts.length) {
          setSelectedAccountId(String(nextAccounts[0].id));
          setBridgeToNumber(nextAccounts[0].default_bridge_number || "");
          setRecordingEnabled(Boolean(nextAccounts[0].supports_recording));
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || err?.message || "Failed to load telephony options.");
      })
      .finally(() => {
        if (!cancelled) setTelephonyLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, conversation]);

  useEffect(() => {
    if (!selectedAccount) return;

    setBridgeToNumber((prev) => prev || selectedAccount.default_bridge_number || "");
    if (!selectedAccount.supports_recording) {
      setRecordingEnabled(false);
    }
  }, [selectedAccount]);

  const handleSave = async () => {
    if (!conversation?.id || !selectedAccountId) return;

    setSaving(true);
    setError("");

    try {
      const res = await omnichannelInboxApi.initiateVoiceCall(conversation.id, {
        telephony_account_id: Number(selectedAccountId),
        to_number: toNumber.trim() || null,
        bridge_to_number: bridgeToNumber.trim() || null,
        recording_enabled: recordingEnabled,
      });

      onSaved?.(res);

      if (res?.telephony?.handoff_url) {
        window.location.assign(res.telephony.handoff_url);
      }

      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to initiate voice call.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Start Live Call</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.6}>
          <Typography variant="body2" color="text.secondary">
            Start a live outbound customer call using a desktop handoff or cloud telephony account from the unified inbox.
          </Typography>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <TextField
            select
            label="Telephony account"
            value={selectedAccountId}
            onChange={(e) => setSelectedAccountId(e.target.value)}
            disabled={telephonyLoading || saving}
            fullWidth
            helperText={telephonyLoading ? "Loading telephony options..." : "Choose the telephony mode for this call."}
          >
            {telephonyAccounts.map((account) => (
              <MenuItem key={account.id} value={String(account.id)}>
                {account.provider} · {account.mode}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Customer number"
            value={toNumber}
            onChange={(e) => setToNumber(e.target.value)}
            fullWidth
          />

          {selectedAccount?.mode === "cloud" ? (
            <TextField
              label="Bridge number"
              value={bridgeToNumber}
              onChange={(e) => setBridgeToNumber(e.target.value)}
              fullWidth
              helperText={selectedAccount?.requires_bridge_number ? "Required for cloud calling on this account." : "Optional if a default bridge number is already configured."}
            />
          ) : null}

          <FormControlLabel
            control={
              <Switch
                checked={recordingEnabled}
                onChange={(e) => setRecordingEnabled(e.target.checked)}
                disabled={!selectedAccount?.supports_recording}
              />
            }
            label={selectedAccount?.supports_recording ? "Enable recording" : "Recording is not available on this account"}
          />

          {selectedAccount ? (
            <Alert severity="info">
              {selectedAccount.mode === "desktop_handoff"
                ? "Desktop handoff will open the customer number in the local phone handler or softphone."
                : "Cloud mode will ask Twilio to call the customer and bridge the call to the configured agent number."}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || telephonyLoading || !selectedAccountId || !toNumber.trim()}>
          {saving ? "Starting..." : "Start Call"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function VoiceCallDialog({ open, onClose, conversation, onSaved }) {
  const [direction, setDirection] = useState("outbound");
  const [status, setStatus] = useState("completed");
  const [fromNumber, setFromNumber] = useState("");
  const [toNumber, setToNumber] = useState("");
  const [startedAt, setStartedAt] = useState("");
  const [endedAt, setEndedAt] = useState("");
  const [durationSeconds, setDurationSeconds] = useState("");
  const [disposition, setDisposition] = useState("");
  const [wrapUpNote, setWrapUpNote] = useState("");
  const [recordingEnabled, setRecordingEnabled] = useState(false);
  const [createFollowUp, setCreateFollowUp] = useState(false);
  const [followUpDueAt, setFollowUpDueAt] = useState("");
  const [followUpNote, setFollowUpNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setDirection("outbound");
      setStatus("completed");
      setFromNumber("");
      setToNumber("");
      setStartedAt("");
      setEndedAt("");
      setDurationSeconds("");
      setDisposition("");
      setWrapUpNote("");
      setRecordingEnabled(false);
      setCreateFollowUp(false);
      setFollowUpDueAt("");
      setFollowUpNote("");
      setSaving(false);
      setError("");
      return;
    }

    setDirection("outbound");
    setStatus("completed");
    setFromNumber("");
    setToNumber(conversation?.customer?.phone || conversation?.contact?.phone || "");
    setStartedAt(new Date().toISOString().slice(0, 16));
    setEndedAt(new Date().toISOString().slice(0, 16));
    setDurationSeconds("");
    setDisposition("");
    setWrapUpNote("");
    setRecordingEnabled(false);
    setCreateFollowUp(false);
    setFollowUpDueAt("");
    setFollowUpNote("");
    setError("");
  }, [open, conversation]);

  const handleSave = async () => {
    if (!conversation?.id) return;

    setSaving(true);
    setError("");

    try {
      let resolvedDuration = durationSeconds ? Number(durationSeconds) : null;
      if (!resolvedDuration && startedAt && endedAt) {
        const diffMs = new Date(endedAt).getTime() - new Date(startedAt).getTime();
        if (diffMs > 0) {
          resolvedDuration = Math.round(diffMs / 1000);
        }
      }

      const res = await omnichannelInboxApi.logVoiceCall(conversation.id, {
        direction,
        status,
        from_number: fromNumber.trim() || null,
        to_number: toNumber.trim() || null,
        started_at: startedAt ? new Date(startedAt).toISOString() : null,
        ended_at: endedAt ? new Date(endedAt).toISOString() : null,
        duration_seconds: resolvedDuration,
        recording_enabled: recordingEnabled,
        disposition: disposition.trim() || null,
        wrap_up_note: wrapUpNote.trim() || null,
        create_follow_up: createFollowUp,
        follow_up_due_at: createFollowUp && followUpDueAt ? new Date(followUpDueAt).toISOString() : null,
        follow_up_note: createFollowUp ? (followUpNote.trim() || wrapUpNote.trim() || null) : null,
      });
      onSaved?.(res);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to log call.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Log Call</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.6}>
          <Typography variant="body2" color="text.secondary">
            Capture manual call activity, wrap-up context, and any required follow-up directly on this conversation.
          </Typography>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <Stack direction={{ xs: "column", md: "row" }} spacing={1.2}>
            <TextField
              select
              label="Direction"
              value={direction}
              onChange={(e) => setDirection(e.target.value)}
              fullWidth
            >
              <MenuItem value="outbound">Outbound</MenuItem>
              <MenuItem value="inbound">Inbound</MenuItem>
            </TextField>

            <TextField
              select
              label="Call status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              fullWidth
            >
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="missed">Missed</MenuItem>
              <MenuItem value="no_answer">No answer</MenuItem>
              <MenuItem value="voicemail">Voicemail</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="logged">Logged</MenuItem>
            </TextField>
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={1.2}>
            <TextField
              label="From number"
              value={fromNumber}
              onChange={(e) => setFromNumber(e.target.value)}
              fullWidth
            />
            <TextField
              label="To number"
              value={toNumber}
              onChange={(e) => setToNumber(e.target.value)}
              fullWidth
            />
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={1.2}>
            <TextField
              label="Started at"
              type="datetime-local"
              value={startedAt}
              onChange={(e) => setStartedAt(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Ended at"
              type="datetime-local"
              value={endedAt}
              onChange={(e) => setEndedAt(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Duration (seconds)"
              type="number"
              value={durationSeconds}
              onChange={(e) => setDurationSeconds(e.target.value)}
              fullWidth
            />
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} alignItems={{ md: "center" }}>
            <TextField
              select
              label="Disposition"
              value={disposition}
              onChange={(e) => setDisposition(e.target.value)}
              fullWidth
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="callback_required">Callback required</MenuItem>
              <MenuItem value="follow_up_required">Follow-up required</MenuItem>
              <MenuItem value="left_voicemail">Left voicemail</MenuItem>
              <MenuItem value="escalate">Escalate</MenuItem>
              <MenuItem value="resolved">Resolved on call</MenuItem>
            </TextField>
            <FormControlLabel
              control={<Switch checked={recordingEnabled} onChange={(e) => setRecordingEnabled(e.target.checked)} />}
              label="Recording enabled"
            />
          </Stack>

          <TextField
            label="Wrap-up note"
            value={wrapUpNote}
            onChange={(e) => setWrapUpNote(e.target.value)}
            fullWidth
            multiline
            minRows={4}
          />

          <FormControlLabel
            control={<Switch checked={createFollowUp} onChange={(e) => setCreateFollowUp(e.target.checked)} />}
            label="Create follow-up from this call"
          />

          {createFollowUp ? (
            <Stack spacing={1.2}>
              <TextField
                label="Follow-up due at"
                type="datetime-local"
                value={followUpDueAt}
                onChange={(e) => setFollowUpDueAt(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Follow-up note"
                value={followUpNote}
                onChange={(e) => setFollowUpNote(e.target.value)}
                fullWidth
                multiline
                minRows={3}
              />
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? "Saving..." : "Log Call"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function DeleteConversationDialog({ open, onClose, conversation, onDeleted }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleDelete = async () => {
    if (!conversation?.id) return;
    setSaving(true);
    setError("");
    try {
      await omnichannelInboxApi.bulkConversationAction({
        conversation_ids: [conversation.id],
        action: "delete",
      });
      onDeleted?.(conversation.id);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to delete conversation.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete Conversation</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <Typography variant="body2">
            Are you sure you want to permanently delete this conversation with{" "}
            <strong>{conversation?.customer?.name || conversation?.customer_name || "this contact"}</strong>?
          </Typography>
          <Typography variant="body2" color="error.main">
            This will delete all messages and cannot be undone.
          </Typography>
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleDelete} variant="contained" color="error" disabled={saving}>
          {saving ? "Deleting..." : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
